import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react'
import { Link } from 'react-router'
import { addons, costMicroEur } from '@fjalim/shared'
import { transcriptions, type Transcription, type TranscriptionOptions } from '../api'
import { useAuth } from '../auth/AuthContext'
import { Modal } from '../components/Modal'
import { useI18n } from '../i18n'
import { durationBucket, track } from '../lib/analytics'
import { eurPerMinuteToMicroEur } from '../lib/format'
import { formatDuration } from '../lib/timecode'
import { RowExport } from './RowExport'
import { useUploads } from './useUploads'
import styles from './Transcriptions.module.css'

const POLL_MS = 4000
const ACTIVE = new Set(['queued', 'processing'])
/** `max_speakers` is a hint the worker may ignore; the API accepts 2..10 (spec §12). */
const MAX_SPEAKER_CHOICES = [2, 3, 4, 5, 6, 7, 8, 9, 10]

export function TranscriptionsPage() {
  const { t, tn, eur, date, longDate, errorText } = useI18n()
  const { account, refresh } = useAuth()
  const [rows, setRows] = useState<Transcription[] | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [toDelete, setToDelete] = useState<Transcription | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [speakers, setSpeakers] = useState(false)
  /** 0 means "as many as the audio has" — the worker decides. */
  const [maxSpeakers, setMaxSpeakers] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const dragDepth = useRef(0)
  const doneTracked = useRef(new Set<string>())

  const upsert = useCallback((created: Transcription) => {
    setRows((cur) => (cur && cur.some((r) => r.id === created.id) ? cur : [created, ...(cur ?? [])]))
  }, [])
  const sellSpeakers = addons.speakers.available
  const speakerOptions: TranscriptionOptions | undefined =
    sellSpeakers && speakers ? { speakers: true, ...(maxSpeakers >= 2 ? { max_speakers: maxSpeakers } : {}) } : undefined
  const { items, add, dismiss } = useUploads(upsert, speakerOptions)

  // First load.
  useEffect(() => {
    const controller = new AbortController()
    transcriptions
      .list(undefined, controller.signal)
      .then((page) => {
        setRows(page.data)
        setCursor(page.next_cursor)
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setError(errorText(e))
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Poll while something is queued or processing; refresh the balance when a job finishes.
  const hasActive = rows?.some((r) => ACTIVE.has(r.status)) ?? false
  useEffect(() => {
    if (!hasActive) return
    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      try {
        const page = await transcriptions.list(undefined, controller.signal)
        setRows((cur) => {
          if (!cur) return page.data
          const fresh = new Map(page.data.map((r) => [r.id, r]))
          const finished = cur.some((r) => ACTIVE.has(r.status) && fresh.get(r.id) && !ACTIVE.has(fresh.get(r.id)!.status))
          if (finished) void refresh()
          for (const r of cur) {
            const f = fresh.get(r.id)
            if (ACTIVE.has(r.status) && f?.status === 'completed' && !doneTracked.current.has(r.id)) {
              doneTracked.current.add(r.id)
              track('transcription_done', { duration: durationBucket(f.duration_seconds), speakers: f.options.speakers === true })
            }
          }
          const known = new Set(cur.map((r) => r.id))
          return [...page.data.filter((r) => !known.has(r.id)), ...cur.map((r) => fresh.get(r.id) ?? r)]
        })
      } catch {
        // The next tick retries.
      }
    }, POLL_MS)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [hasActive, rows, refresh])

  const loadMore = async () => {
    if (!cursor) return
    try {
      const page = await transcriptions.list(cursor)
      setRows((cur) => [...(cur ?? []), ...page.data])
      setCursor(page.next_cursor)
    } catch (e) {
      setError(errorText(e))
    }
  }

  const confirmDelete = async () => {
    if (!toDelete) return
    setDeleting(true)
    try {
      await transcriptions.remove(toDelete.id)
      setRows((cur) => cur?.filter((r) => r.id !== toDelete.id) ?? cur)
      setToDelete(null)
    } catch (e) {
      setError(errorText(e))
      setToDelete(null)
    } finally {
      setDeleting(false)
    }
  }

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    dragDepth.current = 0
    setDragging(false)
    const files = Array.from(event.dataTransfer.files)
    if (files.length > 0) add(files)
  }

  const durLabels = { h: t('dur.h'), min: t('dur.min'), s: t('dur.s') }
  const lowBalance = account !== null && account.balance_micro_eur < costMicroEur(60)
  const today = longDate()
  const tone = (status: Transcription['status']) =>
    status === 'completed' ? 'ok' : status === 'processing' ? 'warn' : status === 'failed' ? 'danger' : undefined

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="dateline">{today}</p>
          <h1>{t('list.title')}</h1>
        </div>
      </div>

      {lowBalance ? (
        <p className="notice notice-warn">
          {t('list.lowBalance')} <Link to="/billing">{t('list.topup')}</Link>
        </p>
      ) : null}
      {error ? (
        <p className="notice notice-error" role="alert">
          {error}
        </p>
      ) : null}

      <section
        className={`${styles.drop} ${dragging ? styles.dropActive : ''}`}
        onDragEnter={(e) => {
          e.preventDefault()
          dragDepth.current++
          setDragging(true)
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1)
          if (dragDepth.current === 0) setDragging(false)
        }}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          id="file-input"
          className="sr-only"
          type="file"
          multiple
          accept="audio/*,video/*"
          data-testid="file-input"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? [])
            if (files.length > 0) add(files)
            e.target.value = ''
          }}
        />
        <div className={styles.dropMain}>
          <span className={styles.dropIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 16V4M7 9l5-5 5 5M4 20h16" />
            </svg>
          </span>
          <label htmlFor="file-input" className={styles.dropLabel}>
            <span className={styles.dropTitle}>{dragging ? t('list.drop.active') : t('list.drop.title')}</span>
            <span className="hint">{t('list.drop.hint')}</span>
          </label>
          <button type="button" className={`btn btn-primary ${styles.dropButton}`} onClick={() => inputRef.current?.click()}>
            {t('list.upload.cta')}
          </button>
        </div>

        {sellSpeakers ? (
          <div className={styles.options}>
            <label className={styles.option}>
              <input
                type="checkbox"
                checked={speakers}
                onChange={(e) => {
                  setSpeakers(e.target.checked)
                  if (!e.target.checked) setMaxSpeakers(0)
                }}
              />
              <span>{t('list.speakers.toggle', { price: eur(eurPerMinuteToMicroEur(addons.speakers.eurPerMinute)) })}</span>
            </label>
            {speakers ? (
              <label className={styles.option}>
                <span>{t('list.speakers.countLabel')}</span>
                <select
                  className="input"
                  value={String(maxSpeakers)}
                  onChange={(e) => setMaxSpeakers(Number(e.target.value))}
                  aria-label={t('list.speakers.countLabel')}
                >
                  <option value="0">{t('list.speakers.auto')}</option>
                  {MAX_SPEAKER_CHOICES.map((n) => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        ) : null}
      </section>

      {items.length > 0 ? (
        <section aria-labelledby="uploads-h" className={styles.uploads}>
          <h2 id="uploads-h" className="sr-only">
            {t('list.uploadsHeading')}
          </h2>
          <ul className={styles.uploadList} aria-live="polite">
            {items.map((it) => (
              <li key={it.key} className={styles.upload} data-phase={it.phase}>
                <span className={styles.uploadName}>{it.name}</span>
                <span className={styles.uploadState}>
                  {it.phase === 'queued' ? '…' : null}
                  {it.phase === 'uploading' ? t('list.upload.uploading', { pct: Math.round(it.progress * 100) }) : null}
                  {it.phase === 'starting' ? t('list.upload.starting') : null}
                  {it.phase === 'done' ? t('list.upload.done') : null}
                  {it.phase === 'failed' ? <span className={styles.failed}>{it.error ?? t('list.upload.failed')}</span> : null}
                </span>
                <progress className={styles.progress} max={1} value={it.progress} aria-label={it.name} />
                {it.phase === 'done' || it.phase === 'failed' ? (
                  <button type="button" className="btn btn-quiet btn-small" onClick={() => dismiss(it.key)} aria-label={`${t('list.upload.dismiss')}: ${it.name}`}>
                    ×
                  </button>
                ) : (
                  <span />
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {rows === null && !error ? <p role="status">{t('common.loading')}</p> : null}

      {rows !== null && rows.length === 0 ? (
        <section className={styles.empty} aria-labelledby="empty-h">
          <h2 id="empty-h">{t('list.empty.title')}</h2>
          <p>{t('list.empty.body')}</p>
          <ol className={styles.steps}>
            {(['step1', 'step2', 'step3'] as const).map((step, i) => (
              <li key={step}>
                <span className={styles.stepNo} aria-hidden="true">
                  {i + 1}
                </span>
                <span>{t(`list.empty.${step}`)}</span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {rows !== null && rows.length > 0 ? (
        <>
          <div className={styles.head} aria-hidden="true">
            <span>{t('list.col.name')}</span>
            <span>{t('list.col.status')}</span>
            <span className={styles.num}>{t('list.col.duration')}</span>
            <span className={styles.num}>{t('list.col.cost')}</span>
            <span>{t('list.col.created')}</span>
            <span />
          </div>
          <ol className={styles.list} aria-label={t('list.title')}>
            {rows.map((row, index) => (
              <li key={row.id} className={styles.item} style={{ ['--i' as string]: Math.min(index, 14) }}>
                <span className={styles.name}>
                  {row.status === 'completed' ? (
                    <Link to={`/transcriptions/${row.id}`}>{row.filename ?? t('list.untitled')}</Link>
                  ) : (
                    <span>{row.filename ?? t('list.untitled')}</span>
                  )}
                  {row.error ? <span className={styles.errorLine}>{row.error.message}</span> : null}
                  {row.speakers !== null ? (
                    <span className={styles.speakerTag} data-testid={`speakers-${row.id}`}>
                      {tn('list.speakers', row.speakers)}
                    </span>
                  ) : null}
                </span>
                <span className={styles.status}>
                  <span className="chip" data-tone={tone(row.status)}>
                    {t(`list.status.${row.status}`)}
                  </span>
                </span>
                <span className={`${styles.num} ${styles.duration} mono`} data-label={t('list.col.duration')}>
                  {formatDuration(row.duration_seconds, durLabels)}
                </span>
                <span className={`${styles.num} ${styles.cost} mono`} data-label={t('list.col.cost')}>
                  {row.cost_micro_eur === null ? '—' : eur(row.cost_micro_eur)}
                </span>
                <span className={styles.created} data-label={t('list.col.created')}>
                  {date(row.created_at, true)}
                </span>
                <span className={styles.actions}>
                  {row.status === 'completed' ? (
                    <>
                      <Link className="btn btn-small" to={`/transcriptions/${row.id}`}>
                        {t('list.open')}
                      </Link>
                      <RowExport id={row.id} filename={row.filename} onError={setError} />
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-quiet btn-small"
                    onClick={() => setToDelete(row)}
                    aria-label={`${t('list.delete')}: ${row.filename ?? row.id}`}
                  >
                    {t('list.delete')}
                  </button>
                </span>
              </li>
            ))}
          </ol>
          {cursor ? (
            <button type="button" className="btn" onClick={() => void loadMore()}>
              {t('list.more')}
            </button>
          ) : null}
        </>
      ) : null}

      {toDelete ? (
        <Modal
          title={t('list.delete.title')}
          onClose={() => setToDelete(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setToDelete(null)} data-autofocus>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger" disabled={deleting} onClick={() => void confirmDelete()}>
                {t('list.delete.confirm')}
              </button>
            </>
          }
        >
          <p>
            <strong>{toDelete.filename ?? toDelete.id}</strong>
          </p>
          <p>{t('list.delete.body')}</p>
        </Modal>
      ) : null}
    </div>
  )
}
